import { DUE_TONE, dueKey } from '@/domain';
import { labToday } from '@/shared/format/instant';
import styles from './DeliveryDate.module.css';

export interface DeliveryDateProps {
  /** The calendar day, YYYY-MM-DD, as the case carries it. */
  date: string;
  /**
   * A case that is finished (every tray Ready, shipped, received, archived) is
   * never overdue: its date is drawn with no colour. Absent reads as not
   * finished.
   */
  finished?: boolean;
  /**
   * The pulse, on a case's own screens. Off in tables: six pulsing cells in one
   * list is noise, and the status column already carries the tone there.
   */
  emphasis?: boolean;
}

/**
 * The delivery date, everywhere it is read. Section 4 of the request of
 * 13 September 2026.
 *
 * One component rather than a rule written down, because a rule about how a
 * date should look drifts across six screens within a month and a component
 * cannot. The tone is the one rule of src/domain/time/dueDate.ts: red once the
 * date has passed, amber today and tomorrow, no colour after that. The request
 * of 7 October 2026, section 6. The urgent and rework flags are deliberately
 * not read, since this colours the calendar and those two are the case's own
 * status, shown beside it.
 */
export function DeliveryDate({ date, finished = false, emphasis = false }: DeliveryDateProps) {
  const due = dueKey(date, labToday());
  const tone = finished && due === 'overdue' ? 'ok' : DUE_TONE[due];

  const classes = [styles.date];
  if (tone === 'late') classes.push(styles.late);
  if (tone === 'watch') classes.push(styles.watch);
  if (emphasis && tone === 'late') classes.push(styles.pulse);

  // `time` rather than a span: the value is already the machine format the
  // attribute wants, so the markup says what the text is.
  return (
    <time className={classes.join(' ')} dateTime={date}>
      {date}
    </time>
  );
}
